/**
 * Step 3's opening block: the letter's own facts, then whatever facts the
 * selected letter type declares it cannot be written without.
 *
 * Split out of `LetterDetailsStep` under `docs/FILE_SIZE_POLICY.md` — that file
 * crossed 350 lines and had to shrink before the required-documents checklist
 * could be added to it. The seam is deliberate rather than arbitrary: these two
 * sections are *fields*, driven by `required_inputs`, while what remains in the
 * parent is *statements* and the evidence hanging off them. They share no state
 * beyond the form patch callback.
 *
 * `required_inputs` is rendered straight off the type. A new letter type with
 * different inputs needs no change here.
 */
import type { LetterTypeSummary } from '@/components/attest/api'
import type { LetterFormPatch, LetterFormState } from '@/components/attest/formState'
import { EXAMPLE_PLACEHOLDER, RequiredMark } from '@/components/attest/requiredFields'
import { INPUT_FIELD_HELP, WIZARD_FIELD_HELP } from '@/components/attest/wizardHelp'
import { HelpDot } from '@/components/ui/help-dot'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

export interface LetterFactsSectionProps {
  letterType: LetterTypeSummary
  form: LetterFormState
  onChange: (patch: LetterFormPatch) => void
}

export function LetterFactsSection({ letterType, form, onChange }: LetterFactsSectionProps) {
  // Patches a nested container, so it derives that container from `prev` rather
  // than from the `form` prop, which is only a render snapshot. Two patches
  // sharing a snapshot lose the earlier write.
  const setInput = (key: string, value: string) => {
    onChange(prev => ({ inputs: { ...prev.inputs, [key]: value } }))
  }

  return (
    <>
      <section className="grid gap-4 md:grid-cols-2">
        <div>
          {/* `block`, not the shared Label's flex — see `RequiredMark`: a flex
              label pushes the star to the right edge once the text fills the
              row, which at 375px is most of the labels on this step. */}
          <Label className="block" htmlFor="subject-entity">
            Subject entity
            <RequiredMark field="subject-entity" />
          </Label>
          <Input
            id="subject-entity"
            data-testid="subject-entity"
            className="mt-1"
            value={form.subjectEntity}
            onChange={e => onChange({ subjectEntity: e.target.value })}
          />
        </div>
        <div>
          <Label className="block" htmlFor="addressee">
            Addressee
            <RequiredMark field="addressee" />
          </Label>
          <Input
            id="addressee"
            data-testid="addressee"
            className="mt-1"
            value={form.addressee}
            onChange={e => onChange({ addressee: e.target.value })}
          />
        </div>
        <div>
          {/* Recommended, not required (ATTEST-UX-1 D1): no gate refuses an
              empty list, so it gets no asterisk — but leaving it blank widens
              who may rely on the letter, which is worth saying out loud. */}
          <div className="flex items-center gap-1">
            <Label htmlFor="intended-users">Intended users (recommended)</Label>
            <HelpDot {...WIZARD_FIELD_HELP.intendedUsers} testId="info-intendedUsers" />
          </div>
          <Input
            id="intended-users"
            data-testid="intended-users"
            className={`mt-1 ${EXAMPLE_PLACEHOLDER}`}
            placeholder="e.g. Acme Bank, N.A."
            value={form.intendedUsersText}
            onChange={e => onChange({ intendedUsersText: e.target.value })}
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Comma separated. The letter&rsquo;s use is restricted to these parties.
          </p>
        </div>
        <div>
          <Label htmlFor="letter-date">Letter date</Label>
          <Input
            id="letter-date"
            data-testid="letter-date"
            type="date"
            className="mt-1"
            value={form.letterDate}
            onChange={e => onChange({ letterDate: e.target.value })}
          />
          <p className="mt-1 text-xs text-muted-foreground">Defaults to today if left blank.</p>
        </div>
        <div className="md:col-span-2">
          <Label htmlFor="purpose">Purpose (optional)</Label>
          <Textarea
            id="purpose"
            data-testid="purpose"
            rows={2}
            className="mt-1"
            value={form.purpose}
            onChange={e => onChange({ purpose: e.target.value })}
          />
        </div>
      </section>

      {letterType.required_inputs.length > 0 && (
        <section>
          <h3 className="text-sm font-semibold">Facts this letter type requires</h3>
          <p className="mt-1 text-xs text-muted-foreground">
            Missing any of these is a refusal, not a blank in the output.
          </p>
          <div className="mt-3 grid gap-4 md:grid-cols-2">
            {letterType.required_inputs.map(input => (
              <div key={input.key}>
                {/* The inputs are YAML data rendered by a generic loop, so the
                    help is keyed off the input's key. An input with no entry
                    gets no (i) — right default for one a new type adds. */}
                <div className="flex items-center gap-1">
                  <Label className="block" htmlFor={`input-${input.key}`}>
                    {input.label}
                    <RequiredMark field={`input-${input.key}`} />
                  </Label>
                  {INPUT_FIELD_HELP[input.key] && (
                    <HelpDot
                      {...WIZARD_FIELD_HELP[INPUT_FIELD_HELP[input.key]!]}
                      testId={`info-${INPUT_FIELD_HELP[input.key]}`}
                    />
                  )}
                </div>
                <Input
                  id={`input-${input.key}`}
                  data-testid={`input-${input.key}`}
                  className="mt-1"
                  value={form.inputs[input.key] ?? ''}
                  onChange={e => setInput(input.key, e.target.value)}
                />
                {input.description && (
                  <p className="mt-1 text-xs text-muted-foreground">{input.description}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}
    </>
  )
}
